import { useAuthState } from "~/routes/layout";
import { CoachProfileContext } from "~/contexts/coach";
import type { RequestHandler } from "@builder.io/qwik-city";
import { server$ } from "@builder.io/qwik-city";
import {
  $,
  Slot,
  useSignal,
  component$,
  useContextProvider,
  useResource$,
  Resource,
} from "@builder.io/qwik";

// need to handle sign up form in popup
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { SignUp, useFormLoader } from "~/routes/(auth)/signup/";
export { useFormLoader } from "~/routes/(auth)/signup/";
import { Modal } from "~/components/ui/modal/modal";

import { CoachCard } from "~/components/coach-card";
import { Breadcrumb } from "~/components/ui/breadcrumb";
import { SimpleCarousel } from "~/components/ui/simple-carousel";
import { ProfileSkeleton } from "~/components/ui/profile-skeleton";

import { useNavigationLoading } from "~/hooks/use-navigation-loading";

import { getUserPreferences } from "~/lib/user.db";
import { transformUserPreferencesToCoachMatchForm } from "~/lib/transforms/user-preferences";

import type { ProfileSelect } from "~/../drizzle/schema";
import type { CoachSelect, CoachDetailsSelect } from "~/lib/schemas";

type CoachData = {
  coach: CoachSelect;
  profile: ProfileSelect;
  details: CoachDetailsSelect;
  similarity: number;
  expertise: string;
};

import { useCoachData } from "~/shared/loaders";
import { useClerk } from "~/hooks/use-clerk";
import { useUserPreferences } from "../steps/layout";
import { CoachCardSkeleton } from "~/components/coach-card-skeleton";
import { getCoachMatches } from "~/lib/matching";
export { useUserPreferences };
export { useCoachData } from "~/shared/loaders";

export const onRequest: RequestHandler = ({ redirect, sharedMap, cookie }) => {
  const auth = sharedMap.get("auth");
  const userId = cookie.get("userId")?.value as string;

  if (!userId && !auth?.isSignedIn) {
    throw redirect(302, `/discovery`);
  }
};

export const getMatchPreferences = server$(async function () {
  const userId = this.cookie.get("userId")?.value;
  if (!userId) {
    return;
  }

  const userPreferences = await getUserPreferences(userId);
  if (!userPreferences) {
    return;
  }

  return transformUserPreferencesToCoachMatchForm(userPreferences);
});

export default component$(() => {
  const clerk = useClerk();
  const auth = useAuthState();
  const coachSignal = useCoachData();
  const isLoading = useNavigationLoading();
  const showSignupModal = useSignal(false);
  const preferences = useUserPreferences();

  // Provide the coach data to the context
  useContextProvider(CoachProfileContext, coachSignal);

  const coaches = useResource$(async ({ track, cleanup }) => {
    track(() => preferences.value);
    if (!preferences.value) {
      return [];
    }

    console.log("Fetching coaches...");

    const values = preferences.value;
    const controller = new AbortController();
    cleanup(() => controller.abort());

    return getCoachMatches(controller.signal, values, 10, 100);
  });

  const renderItem = $(
    (coachData: CoachData, index: number, isActive: boolean) => (
      <CoachCard
        coachData={coachData}
        isActive={isActive}
        isEnabled={
          Boolean(auth.value.isSignedIn || clerk.value?.isSignedIn) ||
          [2, 3].includes(index)
        }
        href={`/discovery/${coachData.coach.username}`}
        onLockClick$={() => (showSignupModal.value = true)}
        class="h-96"
      />
    ),
  );

  const renderSkeleton = $((_: unknown, i: number) => (
    <CoachCardSkeleton class="animate-in fade-in" seed={i} />
  ));

  return (
    <>
      <div class="mx-auto my-4 flex w-full max-w-screen-xl flex-col overflow-visible px-4 md:mt-8">
        <Breadcrumb
          class="mb-6"
          items={[
            { label: "Home", href: "/" },
            { label: "Coach Discovery", href: "/discovery" },
          ]}
        />
        <h2 class="font-heading mb-6 text-3xl font-medium tracking-tight">
          Your Recommended Coaches
        </h2>
        <Resource
          value={coaches}
          onPending={() => (
            <SimpleCarousel
              items={Array.from({ length: 10 }).fill(null)}
              itemWidth={280}
              selectedIndex={2}
              renderItem={renderSkeleton}
            />
          )}
          onResolved={(data) => (
            <SimpleCarousel
              items={data}
              itemWidth={280}
              selectedIndex={data.findIndex(
                (c) => c.coach.username === coachSignal.value?.username,
              )}
              renderItem={renderItem}
            />
          )}
        />
        {isLoading.value ? <ProfileSkeleton /> : <Slot />}
      </div>
      <Modal.Root bind:show={showSignupModal}>
        <Modal.Panel class="max-w-160 max-h-fit">
          <SignUp
            isModalView={true}
            onSuccess={$(() => {
              showSignupModal.value = false;
            })}
          />
        </Modal.Panel>
      </Modal.Root>
    </>
  );
});
